import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { serverHost } from '../../redux/reducers/reducer';
import RtkStationsTable from '../../components/Dashboard/rtkStations/rtkStationsTable';
import RtkStationConfigDialog from '../../components/Dashboard/rtkStations/rtkStationConfigDialog';
import PageHeader from '../../components/pageHeader/pageHeader';
import '../../styles/tabelsPageStyles.css';

export default function RtkStation() {
  const [rtkStationsData, setRtkStationsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [selectedStation, setSelectedStation] = useState(null);

  const handleConfigureStation = (station) => {
    setSelectedStation(station);
    setConfigDialogOpen(true);
  };

  const handleDeleteStation = (station) => {
    if (!station) return;
    const { station_name, port } = station;

    // Validate we have both name and port
    if (!station_name || !port) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Missing station information. Cannot delete.',
      });
      return;
    }

    Swal.fire({
      title: 'Are you sure you want to delete this station?',
      html: `Station to delete: <b>${station_name}</b> (Port: ${port})`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete!',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#d33',
    }).then((result) => {
      if (result.isConfirmed) {
        const serverhost = serverHost();
        axios.delete(`${serverhost}/rtk/deleteRTKStation`, {
          data: {
            station_name: station_name,
            station_port: port
          },
          withCredentials: true
        })
        .then(res => {
          if (res.status === 200) {
            Swal.fire({
              icon: 'success',
              title: 'Deleted!',
              text: res.data.message || 'Station deleted successfully.',
            });
            // Refresh the stations data after successful deletion
            getAllRtkStations();
          }
        })
        .catch(err => {
          console.error('Error deleting station:', err);
          const errorMessage = err.response?.data?.message || 
                              err.response?.data?.error || 
                              err.message || 
                              'Failed to delete station';
          Swal.fire({
            icon: 'error',
            title: 'Delete Failed',
            text: errorMessage,
          });
        });
      }
    });
  };

  const getAllRtkStations = () => {
    setLoading(true);
    const serverhost = serverHost();
    // Use getRtkStationsStatus to get full station data from ntrip_api
    axios.get(`${serverhost}/rtk/getRtkStationsStatus`, { withCredentials: true })
    .then(res => {
      if(res.status === 200) {
        const data = [];
        // The response has a 'stations' property that contains the array
        const stations = res.data.stations || [];
        stations.forEach(rtk => {
          const object = {
            id: rtk.socket_address ?? rtk.port, // Use port as unique identifier for MUI DataGrid
            station_name: rtk.station_name,
            port: rtk.port,
            status: rtk.status,
            is_active: rtk.is_active,
            is_registered: rtk.is_registered,
            coordinates: {
              lat: rtk.coordinates?.lat || 0,
              lon: rtk.coordinates?.lon || 0,
              alt: rtk.coordinates?.alt || 0,
            },
            sw_version: rtk.sw_version,
            ublox_version: rtk.ublox_version,
            gps_connected: rtk.gps_connected,
            configure: handleConfigureStation, // Add configure function
            delete: handleDeleteStation, // Add delete function
          }
          data.push(object);
        });
        setRtkStationsData(data);
        setError(null);
    } else{
        setRtkStationsData([]);
        setError('Failed to load RTK stations');
      }
    }).catch(err => {
        console.error(err);
        setRtkStationsData([]);
        setError('Error loading RTK stations');
    }).finally(() => {
        setLoading(false);
    })
  }

  const handleConfigDialogClose = (success, stationName = null) => {
    setConfigDialogOpen(false);
    setSelectedStation(null);
    if (success && stationName) {
      // Show success dialog first, then refresh after user clicks OK
      Swal.fire({
        title: 'Success!',
        html: `New station <b>${stationName}</b> registered successfully!`,
        icon: 'success',
        confirmButtonText: 'OK',
        confirmButtonColor: '#3085d6'
      }).then((result) => {
        if (result.isConfirmed) {
          // Refresh the stations list after user acknowledges success
          getAllRtkStations();
        }
      });
    } else if (success) {
      // If success but no stationName, just refresh (for other operations like update)
      getAllRtkStations();
    }
  };

  useEffect(() => {
    getAllRtkStations();
  }, []);


  return (
    <div className="bodyContainer">
      <PageHeader 
        text={"RTK Stations"} 
        buttonsContainer={null}
      />
      <RtkStationsTable data={rtkStationsData} loading={loading} />
      
      <RtkStationConfigDialog
        open={configDialogOpen}
        onClose={handleConfigDialogClose}
        station={selectedStation}
        onRefresh={getAllRtkStations}
      />
    </div>
  );
}